import { parseAsArrayOf, parseAsString, useQueryStates } from 'nuqs'

// Define all filter parsers for purchase orders
const purchaseOrderFilterParsers = {
  // Search query
  search: parseAsString.withDefault('').withOptions({
    clearOnDefault: true,
  }),

  // Date filters
  issueDateFrom: parseAsString.withDefault('').withOptions({
    clearOnDefault: true,
  }),
  issueDateTo: parseAsString.withDefault('').withOptions({
    clearOnDefault: true,
  }),

  // Amount range filters
  amountFrom: parseAsString.withDefault('').withOptions({
    clearOnDefault: true,
  }),
  amountTo: parseAsString.withDefault('').withOptions({
    clearOnDefault: true,
  }),

  // Product total range filters
  productTotalFrom: parseAsString.withDefault('').withOptions({
    clearOnDefault: true,
  }),
  productTotalTo: parseAsString.withDefault('').withOptions({
    clearOnDefault: true,
  }),

  // Status filters
  orderStatus: parseAsArrayOf(parseAsString, ',').withDefault(['all']).withOptions({
    clearOnDefault: true,
  }),
  paymentStatus: parseAsArrayOf(parseAsString, ',').withDefault(['all']).withOptions({
    clearOnDefault: true,
  }),

  // Client filter
  clientId: parseAsArrayOf(parseAsString, ',').withDefault(['all']).withOptions({
    clearOnDefault: true,
  }),
}

export function usePurchaseOrderFilters() {
  const [filters, setFilters] = useQueryStates(purchaseOrderFilterParsers, {
    history: 'replace', // Use replace to avoid cluttering browser history
    shallow: true, // Client-side only updates
  })

  // Helper functions for easier state management
  const updateSearch = (search: string) => {
    setFilters((prev) => ({ ...prev, search }))
  }

  const updateIssueDateFrom = (issueDateFrom: string) => {
    setFilters((prev) => ({ ...prev, issueDateFrom }))
  }

  const updateIssueDateTo = (issueDateTo: string) => {
    setFilters((prev) => ({ ...prev, issueDateTo }))
  }

  const updateAmountFrom = (amountFrom: string) => {
    setFilters((prev) => ({ ...prev, amountFrom }))
  }

  const updateAmountTo = (amountTo: string) => {
    setFilters((prev) => ({ ...prev, amountTo }))
  }

  const updateProductTotalFrom = (productTotalFrom: string) => {
    setFilters((prev) => ({ ...prev, productTotalFrom }))
  }

  const updateProductTotalTo = (productTotalTo: string) => {
    setFilters((prev) => ({ ...prev, productTotalTo }))
  }

  const updateOrderStatus = (orderStatus: string[]) => {
    setFilters((prev) => ({ ...prev, orderStatus }))
  }

  const updatePaymentStatus = (paymentStatus: string[]) => {
    setFilters((prev) => ({ ...prev, paymentStatus }))
  }

  const updateClientId = (clientId: string[]) => {
    setFilters((prev) => ({ ...prev, clientId }))
  }

  const clearAllFilters = () => {
    setFilters({
      search: '',
      issueDateFrom: '',
      issueDateTo: '',
      amountFrom: '',
      amountTo: '',
      productTotalFrom: '',
      productTotalTo: '',
      orderStatus: ['all'],
      paymentStatus: ['all'],
      clientId: ['all'],
    })
  }

  // Check if any filters are active
  const hasActiveFilters = 
    filters.search !== '' ||
    filters.issueDateFrom !== '' ||
    filters.issueDateTo !== '' ||
    filters.amountFrom !== '' ||
    filters.amountTo !== '' ||
    filters.productTotalFrom !== '' ||
    filters.productTotalTo !== '' ||
    !filters.orderStatus.includes('all') ||
    !filters.paymentStatus.includes('all') ||
    !filters.clientId.includes('all')

  return {
    filters,
    setFilters,
    updateSearch,
    updateIssueDateFrom,
    updateIssueDateTo,
    updateAmountFrom,
    updateAmountTo,
    updateProductTotalFrom,
    updateProductTotalTo,
    updateOrderStatus,
    updatePaymentStatus,
    updateClientId,
    clearAllFilters,
    hasActiveFilters,
  }
}
